import log from 'loglevel';
import lang from '../util/lang';

const siteTier = process.env.REACT_APP_TIER || 'stg';
log.setLevel(siteTier === 'prd' ? 'error' : 'debug');

//////////////////
/// STATE DEFAULTS
const initState = {
  widgetName: 'cm',
  isWidgetOpen: true,
  gtmAnalyticsPushed: '',
  curPage: 'signInSpinPage',
  prevPage: null,
  userSession: null,
  langStrings: lang.en,
  hasAgentLoaded: false,
  isAgentAvailable: false, // Handle if chat agent is available
  caseDescription: '', // For Create a Case
  errorType: null,
  caseNumber: null,
  userSessionSign: {
    sign: null,
    timestamp: null,
  },
  hasError: {
    // by default field has no error.
    authEmail: false, // Email not associated with entered Contract Number.
    invalidEmail: false, // entered CM auth email is not in valid format.
    currentCM: false,
    fName: false,
    lName: false,
    newCMEmail: false,
    isBicAsset: false, // enable blue banner only if this value is true.
    contractNumberError: false,
  },
  currentCMDetails: {
    contractNumber: '',
    encryptedCMAuthEmail: '',
    authEmail: '',
    showCMEmailField: false, // CM specifically
    loading: false, // CM loading is false initially.
    showCheckmark: false,
  },
  newCmDetails: {
    email: '',
    firstName: '',
    lastName: '',
    phone: '',
  },
  showForm: {
    showEnteredCM: false, // CM specifically
  },
  authorized: false, // authorize to make change CM request Page 4.
  error: null,
  loadingMoreThanRequired: false, // to show spin message update.
  expandFooterOnError: false,
  success: false,
};

///////////////////
/// Lanuage Options
const langSelected = 'en'; // Get this value from the website this widget is integrated into
const availableLangList = [
  'de',
  'es',
  'fr',
  'it',
  'ja',
  'ko',
  'pl',
  'pt',
  'ru',
  'tr',
  'zh',
];

const languageMap = {
  de: 'German',
  es: 'Spanish',
  fr: 'French',
  it: 'Italian',
  ja: 'Japanese',
  ko: 'Korean',
  pl: 'English', // English
  pt: 'Portuguese Brazillian',
  ru: 'Russian',
  tr: 'English', // English
  zh: 'Chinese (simplified)',
  en: 'English',
};

///////////////////////////////////
/// Container Defined Configuration
const { href, search } = window.location;

// Get oxygenCallbackUrl
let oxygenPath = href.slice(); // copy str
if (oxygenPath.includes('?')) {
  oxygenPath = oxygenPath.substr(0, oxygenPath.indexOf('?'));
}
oxygenPath = `${oxygenPath.substr(0, oxygenPath.lastIndexOf('/'))}/`;
const oxygenCallbackUrl = `${oxygenPath}oxygen_openid_response.html`;

// Obtain query paremeters from current url
const searchParams = new URLSearchParams(search);

// Detect language override parameter
// We only use 2 letter language codes and drop the locale component
//   ex: "zh", "zh-hans", "zh_HANS" ===> "zh"
let languageParam = searchParams.get('language') || langSelected;
const aknLangSelected = languageParam.split(/[-_]/, 1)[0];

// Get URI encoded parentUrl parameter or default to current url
let parentParam = searchParams.get('parentUrl') || href;
try {
  parentParam = decodeURI(parentParam);
} catch (err) {
  log.warn(`[DHX-ETE-CMW][AppConfig] Invalid parentUrl: ${parentParam}`);
  parentParam = href;
}
const parentUrl = parentParam.slice(); // copy
log.debug('parentURL', parentUrl);

// ETE2-3505 - Links to widget from AVA use a special suborigin
const isAvaSubOrigin = !!searchParams.get('fromava');

/////////////////////
/// SFDC CASE OPTIONS
const caseLanguage = languageMap[aknLangSelected] || languageMap[langSelected];
log.debug('caseLanguage', caseLanguage);

const caseDetails = {
  caseStatus: 'Open - Unassigned',
  topic: 'Change SC or CM',
  type: 'Account Management',
  followUpReq: false,
  origin: 'Corporate Website',
  subOrigin: isAvaSubOrigin ? 'AVA Case' : 'AKN Cases',
  systemName: 'VA',
  subject: lang.en.cm.welcome.modalTitle,
  Country: 'US',
  caseLanguage,
  language: caseLanguage,
  cmOrSc: 'Contract Manager',
};
log.debug('caseDetails', caseDetails);

const caseDefaultDetails = {
  caseArea: 'Registration and Activation',
  description: lang.en.cm.welcome.modalTitle,
  origin: 'Corporate Website',
  subOrigin: isAvaSubOrigin ? 'AVA Transaction' : 'AKN Transactions',
  subject: lang.en.cm.welcome.modalTitle,
  topic: 'Change SC or CM',
  caseLanguage,
  type: 'Account Management',
};
log.debug('caseDefaultDetails', caseDefaultDetails);

/////////////////////////////////
/// PROD / STG TIER CONFIGURATION
const siteTierConfig = {
  button_ids: {
    en: '573300000001bHh', // english
    zh: '573300000001bHf', // Simplified Chinese
    fr: '573300000001bHh', // French
    de: '573300000001bHj', // german
    ja: '573300000001bHl', // japanese
    pl: '573300000001bHh', // polish
    pt: '573300000001bHh', // Brazilian Portuguese
    ru: '573300000001bHh', // Russian
    es: '573300000001bHh', // Spanish
    tr: '573300000001bHh', // Turkish
    it: '573300000001bHh', // Italian
    ko: '573300000001bHm', // Korean
  },
  routerLambdaUrl: 'https://ppau67awad.execute-api.us-east-1.amazonaws.com/PROD',
};

const chatDetails = {
  liveAgentId: siteTierConfig.button_ids[aknLangSelected],
  typeId: '012300000019SXBAA2',
  topic: 'Contract Administration',
  widgetOrigin: 'Corporate Website',
  chatSubOrigin: isAvaSubOrigin ? 'AVA Chats' : 'AKN Chats',
  deploymentUrl: 'https://c.la1-c2-phx.salesforceliveagent.com/content/g/js/42.0/deployment.js',
  initUrl: 'https://d.la1-c2-phx.salesforceliveagent.com/chat',
  initParamOne: '572300000001aGj',
  initParamTwo: '00D300000008uIL',
};

// Overrides for DEV / STAGE
if (siteTier !== 'prd') {
  siteTierConfig.routerLambdaUrl = 'https://xy1e932aff.execute-api.us-east-1.amazonaws.com/STAGE';
  chatDetails.deploymentUrl = 'https://c.la1-c1cs-ia5.salesforceliveagent.com/content/g/js/52.0/deployment.js';
  chatDetails.initUrl = 'https://d.la1-c1cs-ia5.salesforceliveagent.com/chat'
  chatDetails.initParamOne = '572300000001aGj';
  chatDetails.initParamTwo = '00D2C00000014ds';
};

export {
  aknLangSelected,
  caseDetails,
  caseDefaultDetails,
  langSelected,
  availableLangList,
  chatDetails,
  siteTierConfig,
  oxygenCallbackUrl,
  initState,
  parentUrl,
};
